import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Loader2, Sparkles } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import { cognitoService } from '@/services/cognito.service';
import nimbusLogo from '@/assets/nimbus-brand/nimbus-ico.svg';

const trialSignupSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(50),
  last_name: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Please enter a valid email'),
  clinic_name: z.string().min(2, 'Clinic name is required').max(255),
  password: z.string().min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Must contain an uppercase letter')
    .regex(/[a-z]/, 'Must contain a lowercase letter')
    .regex(/[0-9]/, 'Must contain a number')
    .regex(/[^A-Za-z0-9]/, 'Must contain a special character'),
  confirm_password: z.string(),
}).refine(data => data.password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

type TrialSignupForm = z.infer<typeof trialSignupSchema>;

const TrialSignup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const trialToken = searchParams.get('token') || undefined;

  const form = useForm<TrialSignupForm>({
    resolver: zodResolver(trialSignupSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      clinic_name: '',
      password: '',
      confirm_password: '',
    },
  });

  const onSubmit = async (data: TrialSignupForm) => {
    setIsSubmitting(true);
    try {
      const response = await api.post('/clinic-onboarding/trial-signup', {
        ...data,
        trial_token: trialToken,
      });

      if (response.data.success) {
        // Auto-login via Cognito
        try {
          await cognitoService.login(data.email, data.password);
          navigate('/');
        } catch {
          toast({
            title: 'Account created!',
            description: 'Your trial is ready. Please log in.',
          });
          navigate('/login');
        }
      }
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || 'Signup failed. Please try again.';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1816] text-[#F5F0EA] flex items-center justify-center p-4 relative overflow-hidden bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(42,157,143,0.04),transparent_50%)]">
      <div className="max-w-[620px] w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo */}
          <div className="flex justify-center mb-12">
            <motion.img
              src={nimbusLogo}
              alt="Nimbus Healthcare"
              className="h-12 w-auto"
              whileHover={{ scale: 1.05 }}
            />
          </div>

          {/* Section Label */}
          <div className="text-center mb-6">
            <p className="font-['Montserrat'] text-xs font-semibold tracking-[0.15em] uppercase text-[#2A9D8F]">
              Free Trial
            </p>
          </div>

          {/* Heading */}
          <h1 className="font-['Fraunces'] text-[clamp(28px,4vw,40px)] font-normal text-[#F5F0EA] text-center mb-3">
            Start your free 7-day trial
          </h1>

          {/* Subtitle */}
          <p className="font-['Inter'] text-base text-[#F5F0EA]/50 text-center mb-10">
            Explore the Nimbus Providers platform with sample data. No credit card required.
          </p>

          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* First Name & Last Name */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#F5F0EA] font-semibold text-base font-['Inter']">First Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="John"
                          className="bg-transparent border border-[#F5F0EA]/15 rounded-2xl px-5 py-4 text-[#F5F0EA] placeholder:text-[#F5F0EA]/40 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]/20"
                        />
                      </FormControl>
                      <FormMessage className="text-[#D4574E] text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#F5F0EA] font-semibold text-base font-['Inter']">Last Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Doe"
                          className="bg-transparent border border-[#F5F0EA]/15 rounded-2xl px-5 py-4 text-[#F5F0EA] placeholder:text-[#F5F0EA]/40 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]/20"
                        />
                      </FormControl>
                      <FormMessage className="text-[#D4574E] text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Email */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#F5F0EA] font-semibold text-base font-['Inter']">Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="john.doe@clinic.com"
                        className="bg-transparent border border-[#F5F0EA]/15 rounded-2xl px-5 py-4 text-[#F5F0EA] placeholder:text-[#F5F0EA]/40 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]/20"
                      />
                    </FormControl>
                    <FormMessage className="text-[#D4574E] text-xs" />
                  </FormItem>
                )}
              />

              {/* Clinic Name */}
              <FormField
                control={form.control}
                name="clinic_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#F5F0EA] font-semibold text-base font-['Inter']">Clinic Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="My Wellness Clinic"
                        className="bg-transparent border border-[#F5F0EA]/15 rounded-2xl px-5 py-4 text-[#F5F0EA] placeholder:text-[#F5F0EA]/40 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]/20"
                      />
                    </FormControl>
                    <FormMessage className="text-[#D4574E] text-xs" />
                  </FormItem>
                )}
              />

              {/* Password */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#F5F0EA] font-semibold text-base font-['Inter']">Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="••••••••"
                        className="bg-transparent border border-[#F5F0EA]/15 rounded-2xl px-5 py-4 text-[#F5F0EA] placeholder:text-[#F5F0EA]/40 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]/20"
                      />
                    </FormControl>
                    <FormMessage className="text-[#D4574E] text-xs" />
                  </FormItem>
                )}
              />

              {/* Confirm Password */}
              <FormField
                control={form.control}
                name="confirm_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#F5F0EA] font-semibold text-base font-['Inter']">Confirm Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="••••••••"
                        className="bg-transparent border border-[#F5F0EA]/15 rounded-2xl px-5 py-4 text-[#F5F0EA] placeholder:text-[#F5F0EA]/40 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]/20"
                      />
                    </FormControl>
                    <FormMessage className="text-[#D4574E] text-xs" />
                  </FormItem>
                )}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#B87A5D] hover:bg-[#A56A4D] text-white font-['Montserrat'] font-medium py-6 rounded-full transition-all duration-200"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creating your trial...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Start Free Trial
                  </>
                )}
              </Button>

              {/* Login Link */}
              <p className="text-center text-[#F5F0EA]/50 text-sm font-['Inter'] mt-6">
                Already have an account?{' '}
                <a
                  href="/login"
                  className="text-[#2A9D8F] hover:text-[#238E7E] font-medium transition-colors"
                >
                  Log in
                </a>
              </p>
            </form>
          </Form>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-6 mt-10">
            <div className="flex items-center gap-2 text-[#F5F0EA]/40 text-xs font-['Inter']">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-[#F5F0EA]/40 text-xs font-['Inter']">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span>Secure & Encrypted</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TrialSignup;
